import { RESOURCES } from "@/constants/resources";
import { useActionJobs } from "@/contexts/ActionJobsContext";
import { resourceSelectionLabel } from "@/helpers/ResourceHelpers";
import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import { useColumnOrder } from "@/hooks/useColumnOrder";
import { useColumnVisibility } from "@/hooks/useColumnVisibility";
import { SELECT_COLUMN_ID, useRowSelection } from "@/hooks/useRowSelection";
import { useTablePageSize } from "@/hooks/useTablePageSize";
import ITableProps, { ITableMethods } from "@/interfaces/Table";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Column,
  Header,
  TableState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Card,
  Col,
  Form,
  Offcanvas,
  Row,
  Spinner,
  Table as RBTable,
} from "react-bootstrap";
import SimpleBar from "simplebar-react";
import BulkDeleteAction from "./BulkDeleteAction";
import DraggableRow from "./DraggableRow";
import TableBulkBar from "./TableBulkBar";
import TableCellContent from "./TableCellContent";
import TablePagination from "./TablePagination";

const defaultSelectionLabel = (count: number) =>
  count > 1 ? "lignes sélectionnées" : "ligne sélectionnée";

const Table: ForwardRefRenderFunction<ITableMethods, ITableProps> = (
  {
    columns,
    data = [],
    HeaderComponent,
    onStateChange,
    smallFilters = false,
    tableName,
    onDndChange,
    state,
    meta = {
      total: 0,
      lastPage: 15,
    },
    FilterComponent,
    isLoading = false,
    enableRowSelection = true,
    selectionLabel,
    resource,
    BulkActionsComponent,
  },
  ref,
) => {
  const isSelectable = !!enableRowSelection;
  const labelSelection =
    selectionLabel ??
    (resource
      ? resourceSelectionLabel(RESOURCES[resource])
      : defaultSelectionLabel);
  //* Les lignes engagées dans un job en cours disparaissent sans attendre
  const { hiddenIds, canBulkDelete } = useActionJobs();
  const visibleData = useMemo(() => {
    if (!resource) return data;
    const hidden = hiddenIds(resource);
    if (hidden.size === 0) return data;
    return data.filter((row: any) => !hidden.has(String(row.id)));
  }, [data, resource, hiddenIds]);
  const [enableOrdering, setEnableOrdering] = useState<boolean>(false);
  const topScrollRef = useRef<ElementRef<typeof SimpleBar>>(null);
  const bottomScrollRef = useRef<ElementRef<typeof SimpleBar>>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState<number>(0);
  const triggerEnableOrdering = () => setEnableOrdering((old) => !old);

  //* This is default state variables
  const defaultState = useRef<Partial<TableState>>(state).current;

  const activeSates = Object.keys(state).filter(
    (key) =>
      (state as Record<string, any>)[key] !== null &&
      (state as Record<string, any>)[key] !== undefined &&
      (state as Record<string, any>)[key] !== "" &&
      key !== "pagination" &&
      key !== "columnOrder" &&
      key !== "columnVisibility" &&
      key !== "globalFilter" &&
      key !== "sorting" &&
      (state as Record<string, any>)[key].length > 0,
  );

  //* This is for breakpoints
  const { breakpoints } = useBreakpoints();

  //* This is for bulk selection
  const {
    rowSelection,
    setRowSelection,
    selectColumn,
    selectedIds,
    selectedRows,
    offPageCount,
    clearSelection,
  } = useRowSelection({ enabled: isSelectable, data: visibleData });

  const tableColumns = useMemo(
    () => (isSelectable ? [selectColumn, ...columns] : columns),
    [isSelectable, selectColumn, columns],
  );

  //* This is for show filter or not
  const [showFilters, setShowFilters] = useState(false);
  const handleTriggerFilter = () => setShowFilters((old) => !old);

  //* This is table state
  const {
    getHeaderGroups,
    getRowModel,
    getSelectedRowModel,
    getCanNextPage,
    getAllLeafColumns,
    getCanPreviousPage,
    nextPage,
    previousPage,
    getPageCount,
    setGlobalFilter,
    setPageIndex,
    setPageSize,
    getState,
    setColumnOrder,
    setColumnVisibility,
    getVisibleLeafColumns,
  } = useReactTable({
    // @ts-ignore
    columns: tableColumns,
    data: visibleData,
    //* La colonne de sélection reste épinglée en tête, hors de l'ordre des colonnes
    state: isSelectable
      ? {
          ...state,
          rowSelection,
          columnPinning: { left: [SELECT_COLUMN_ID], right: [] },
        }
      : state,
    // @ts-ignore
    onStateChange,
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    pageCount: meta.lastPage,
    enableSorting: true,
    sortDescFirst: false,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    // @ts-ignore
    getRowId: (row, index) => row.id ?? String(index),
  });

  const notHiddenColumns = useRef<Column<any, unknown>[]>(
    getAllLeafColumns().filter(
      (column) => column.columnDef.enableHiding !== false,
    ),
  ).current;

  //* Use column visibility hook
  const { onColumnVisibilityChange, onChangeVisibilityDefaultState } =
    useColumnVisibility({
      tableName,
      setColumnVisibility,
      defaultState,
      state,
    });

  //* Use column order hook
  const {
    resetColumnOrder,
    updateColumnOrderOnVisibilityChange,
    onMoveHeaderToRight,
    onMoveHeaderToLeft,
  } = useColumnOrder({
    tableName,
    getAllLeafColumns: () =>
      getAllLeafColumns().filter((column) => column.id !== SELECT_COLUMN_ID),
    setColumnOrder,
  });

  //* Changer un filtre ramène à la première page
  const onFiltersChange = useCallback(
    (filters: { [key: string]: any }) =>
      onStateChange((old) => ({
        ...old,
        ...filters,
        pagination: {
          pageSize: old.pagination?.pageSize ?? 15,
          pageIndex: 0,
        },
      })),
    [onStateChange],
  );

  //* Un tri restauré (URL, dernier état connu) peut viser une colonne disparue
  //* depuis : l'API répondrait en erreur à chaque chargement
  useEffect(() => {
    const sortableIds = new Set(
      getAllLeafColumns()
        .filter((column) => column.getCanSort())
        .map((column) => column.id),
    );
    if ((state.sorting ?? []).every(({ id }) => sortableIds.has(id))) return;
    onStateChange((old) => ({
      ...old,
      sorting: (old.sorting ?? []).filter(({ id }) => sortableIds.has(id)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sorting]);

  //* Handle column visibility change with coordination between hooks
  const handleColumnVisibilityChange = (
    columnName: string,
    visibility: boolean,
  ) => {
    onColumnVisibilityChange(columnName, visibility);
    updateColumnOrderOnVisibilityChange(columnName, visibility);
  };

  //* Update table width for top scrollbar
  useEffect(() => {
    const updateWidth = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.scrollWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [data, columns]);

  //* Synchronize horizontal scroll between top and bottom scrollbars
  useEffect(() => {
    const topScrollEl = topScrollRef.current?.getScrollElement();
    const bottomScrollEl = bottomScrollRef.current?.getScrollElement();

    if (!topScrollEl || !bottomScrollEl) return;

    const syncScroll = (source: HTMLElement, target: HTMLElement) => {
      target.scrollLeft = source.scrollLeft;
    };

    const handleTopScroll = () => syncScroll(topScrollEl, bottomScrollEl);
    const handleBottomScroll = () => syncScroll(bottomScrollEl, topScrollEl);

    topScrollEl.addEventListener("scroll", handleTopScroll);
    bottomScrollEl.addEventListener("scroll", handleBottomScroll);

    return () => {
      topScrollEl.removeEventListener("scroll", handleTopScroll);
      bottomScrollEl.removeEventListener("scroll", handleBottomScroll);
    };
  }, [tableWidth]);

  const reorderRow = (draggedRowIndex: number, targetRowIndex: number) =>
    onDndChange?.({
      // @ts-ignore
      fromId: visibleData[draggedRowIndex].id,
      // @ts-ignore
      toId: visibleData[targetRowIndex].id,
    });

  useImperativeHandle(ref, () => ({
    getSelectedRowModel,
    getCanNextPage,
    getCanPreviousPage,
    nextPage,
    previousPage,
    getPageCount,
    setPageIndex,
    resetRowSelection: clearSelection,
  }));

  const { pageSizeOptions, currentPageSize, onPageSizeChange } =
    useTablePageSize({
      tableName,
      total: meta.total,
      pageSize: getState().pagination?.pageSize,
      setPageSize,
      maxPageSize: 300,
    });

  return (
    <Row className="gx-3">
      <Col xl={!!FilterComponent && !smallFilters ? 9 : 12}>
        <Card>
          <Card.Header className="border-bottom border-200 px-0">
            <HeaderComponent
              triggerEnableOrdering={triggerEnableOrdering}
              resetColumnOrder={resetColumnOrder}
              onQueryChange={setGlobalFilter}
              query={state.globalFilter ?? ""}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              notHiddenColumns={notHiddenColumns}
              smallFilters={smallFilters}
              activeFiltersCount={activeSates.length}
              onShowFilters={
                !!FilterComponent ? handleTriggerFilter : undefined
              }
            />
          </Card.Header>
          <Card.Body className="p-0 position-relative">
            {/* Top horizontal scrollbar */}
            <SimpleBar ref={topScrollRef}>
              <div style={{ height: "10px", width: `${tableWidth}px` }}></div>
            </SimpleBar>
            <SimpleBar ref={bottomScrollRef}>
              <RBTable
                ref={tableRef}
                size="sm"
                className="fs--1 mb-0 overflow-hidden"
              >
                <thead className="bg-light text-800 align-middle">
                  {getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        if (header.column.id === SELECT_COLUMN_ID) {
                          return (
                            <th
                              key={header.id}
                              className="formalys-select-cell"
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </th>
                          );
                        }
                        return (
                          <th
                            key={header.id}
                            colSpan={header.colSpan}
                            className="white-space-nowrap pe-6"
                            {...(
                              header.getContext().column.columnDef.meta as any
                            )?.headerProps}
                          >
                            {header.isPlaceholder ? null : (
                              <>
                                <div
                                  {...{
                                    className: header.column.getCanSort()
                                      ? "cursor-pointer select-none"
                                      : "",
                                    onClick:
                                      header.column.getToggleSortingHandler(),
                                  }}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                  {header.column.getCanSort()
                                    ? ({
                                        asc: <span className="sort asc" />,
                                        desc: <span className="sort desc" />,
                                      }[
                                        header.column.getIsSorted() as string
                                      ] ?? <span className="sort" />)
                                    : null}
                                </div>
                                {header.column.getCanPin() &&
                                  enableOrdering && (
                                    <Flex>
                                      {header.column.getIsPinned() !==
                                      "left" ? (
                                        <IconButton
                                          variant="nolina-default"
                                          className="p-1 py-0 me-2"
                                          size="sm"
                                          onClick={() =>
                                            onMoveHeaderToRight(header)
                                          }
                                          icon={faChevronLeft}
                                        />
                                      ) : null}
                                      {header.column.getIsPinned() !==
                                      "right" ? (
                                        <IconButton
                                          className="p-1 py-0"
                                          size="sm"
                                          variant="nolina-default"
                                          onClick={() =>
                                            onMoveHeaderToLeft(header)
                                          }
                                          icon={faChevronRight}
                                        />
                                      ) : null}
                                    </Flex>
                                  )}
                              </>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {getRowModel().flatRows.map((row) => {
                    return !!onDndChange ? (
                      <DraggableRow
                        key={row.id}
                        row={row}
                        reorderRow={reorderRow}
                      />
                    ) : (
                      <tr
                        key={row.id}
                        className={classNames(
                          "btn-reveal-trigger align-middle",
                          { "formalys-row-selected": row.getIsSelected() },
                        )}
                        role="row"
                      >
                        {row.getVisibleCells().map((cell) => {
                          if (cell.column.id === SELECT_COLUMN_ID) {
                            return (
                              <td
                                key={cell.id}
                                role="cell"
                                className="formalys-select-cell"
                              >
                                <TableCellContent cell={cell} />
                              </td>
                            );
                          }
                          // write custom props here
                          return (
                            <td
                              key={cell.id}
                              role="cell"
                              {...(
                                cell.getContext().cell.column.columnDef
                                  .meta as any
                              )?.cellProps}
                              className={classNames(
                                "white-space-nowrap pe-6 py-3",
                                (
                                  cell.getContext().cell.column.columnDef
                                    .meta as any
                                )?.cellProps?.className,
                              )}
                            >
                              <TableCellContent cell={cell} />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </RBTable>
            </SimpleBar>
            {isLoading && (
              <Loader absolute className="bg-white bg-opacity-75 z-index-2" />
            )}
          </Card.Body>
          {isSelectable && selectedIds.length > 0 && (
            <TableBulkBar
              count={selectedIds.length}
              offPageCount={offPageCount}
              label={labelSelection(selectedIds.length)}
              onClear={clearSelection}
            >
              {!!resource && canBulkDelete && (
                <BulkDeleteAction
                  resource={resource}
                  selectedIds={selectedIds}
                  selectedRows={selectedRows}
                  onStarted={clearSelection}
                />
              )}
              {!!BulkActionsComponent && (
                <BulkActionsComponent
                  selectedIds={selectedIds}
                  selectedRows={selectedRows}
                  clearSelection={clearSelection}
                />
              )}
            </TableBulkBar>
          )}
          <Card.Footer className="d-flex justify-content-between">
            <div>
              <Form.Select
                onChange={(e) => {
                  onPageSizeChange(Number(e.target.value));
                }}
                value={currentPageSize}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Form.Select>
            </div>
            <TablePagination
              canNextPage={getCanNextPage()}
              pageCount={getPageCount()}
              setPageIndex={setPageIndex}
              pageIndex={getState().pagination.pageIndex}
              canPreviousPage={getCanPreviousPage()}
              nextPage={nextPage}
              previousPage={previousPage}
            />
          </Card.Footer>
        </Card>
      </Col>
      {!!FilterComponent && (
        <Col xl={3}>
          {smallFilters || breakpoints.down("xl") ? (
            <Offcanvas
              show={showFilters}
              onHide={handleTriggerFilter}
              placement="end"
              className="dark__bg-card-dark"
            >
              <Offcanvas.Header closeButton className="bg-light">
                <h6 className="fs-0 mb-0 fw-semi-bold d-flex align-items-center">
                  Filtres
                  {isLoading && (
                    <Spinner
                      animation="border"
                      size="sm"
                      className="ms-2 text-primary"
                    />
                  )}
                </h6>
              </Offcanvas.Header>
              <FilterComponent
                onFiltersChange={onFiltersChange}
                filters={state}
              />
            </Offcanvas>
          ) : (
            <FilterComponent
              onFiltersChange={onFiltersChange}
              filters={state}
            />
          )}
        </Col>
      )}
    </Row>
  );
};

export default forwardRef(Table);
